// Slipp en fil rett der du står: i bilagsinnboksen ved siden av banklinjene,
// eller på selve banklinja.
//
// Uten dette var veien ut av avstemmingen og inn på Innboks-siden, laste opp
// der, og tilbake igjen — for en linje du så manglet bilag på mens du sto og
// så på den.
//
// ------------------------------------------------------- analyse eller ikke
//
// En fil sluppet i innboksen skal analyseres: ingen vet ennå hva den gjelder,
// og analysen er det som gjør den bokførbar.
//
// En fil sluppet på en linje som ALLEREDE er godkjent eller bokført skal ikke
// analyseres. Linja sier fra før hva kjøpet var, hva det kostet og når det ble
// betalt — analysen ville lest det samme ut av bildet på nytt, brukt en
// AI-kvote på det, og ikke endret noe. Her er filen dokumentasjon, ikke et
// forslag som skal vurderes.
//
// Koblingen skriver selv betalingsbeviset på bilaget: `attach_receipt` i
// functions/bank/bankReconciliationMutationHttp.ts setter `bank-match` med
// kontoen utskriften hører til. Derfor sendes ingen `paymentSourceDecision`
// herfra — en manuell beslutning ville slått ut nettopp det beviset.
(() => {
  const RECEIPT_MIME_PATTERN = /^(image\/|application\/pdf$)/i;
  const RECEIPT_NAME_PATTERN = /\.(pdf|png|jpe?g|webp|gif|bmp|heic|heif)$/i;

  // Er det filer i dette draget? Et bilag som dras fra innboksen bærer bare
  // tekst, og skal ikke utløse en opplasting.
  const hasFilePayload = (event) => {
    try { return Array.from(event?.dataTransfer?.types || []).includes('Files'); }
    catch (error) { return false; }
  };

  // Samme utvalg som filvelgeren i innboksen: bilde eller PDF. Slipper noen en
  // mappe eller et regneark, sier vi det i stedet for å laste opp noe backend
  // ikke kan lese.
  const isReceiptFile = (file) => Boolean(file)
    && (RECEIPT_MIME_PATTERN.test(String(file.type || '')) || RECEIPT_NAME_PATTERN.test(String(file.name || '')));

  const receiptFilesFrom = (fileList) => Array.from(fileList || []).filter(isReceiptFile);

  // En linje som er godkjent, matchet, bokført, ført privat eller korrigert er
  // ferdig behandlet. Speiler `bgHandled` i bank-guided.jsx og
  // `apMonthControlLineHandled` i autopilot-month-control.jsx — de tre må være
  // enige, ellers analyserer den ene et bilag den andre mener er dokumentasjon.
  const HANDLED_STATUSES = new Set(['godkjent', 'matchet', 'bokfort', 'privat', 'korrigert']);
  const bankLineIsHandled = (line) => HANDLED_STATUSES.has(String(line?.status || ''))
    || Boolean(line?._backend?.matchedEntryId);

  const backend = () => globalThis.PlummeBackend || null;

  // Laster opp filene og returnerer bilagene som faktisk kom inn. Én fil som
  // feiler stopper ikke resten — det er samme regel som bunkeopplastingen i
  // innboksen følger.
  const uploadReceiptFiles = async ({ files, analyze = true } = {}) => {
    const list = receiptFilesFrom(files);
    const skipped = Array.from(files || []).length - list.length;
    const uploaded = [];
    let failed = 0;
    for (const file of list) {
      try {
        const saved = await backend()?.uploadReceiptFileToInbox?.(file);
        if (saved?.id) uploaded.push(saved);
        else failed += 1;
      } catch (error) {
        failed += 1;
        console.warn('Bilagsopplasting feilet for en fil.', error);
      }
    }
    if (analyze) {
      for (const entry of uploaded) {
        // En analyse som feiler lar bilaget stå som «Venter på analyse». Det kan
        // kjøres på nytt fra radmenyen i innboksen, og skal ikke stoppe de andre.
        try { await backend()?.analyzeReceiptInboxEntry?.(entry.id, { markPending: false }); }
        catch (error) {
          if (error?.quotaExhausted || error?.rateLimited || error?.providerUnavailable) break;
        }
      }
    }
    return { uploaded, failed, skipped };
  };

  // Slipp på en banklinje: last opp, og koble hvert bilag til linja.
  const dropFilesOnBankLine = async ({ statementId, line, files } = {}) => {
    const analyze = !bankLineIsHandled(line);
    const result = await uploadReceiptFiles({ files, analyze });
    const attached = [];
    let attachFailed = 0;
    for (const entry of result.uploaded) {
      try {
        await backend()?.attachReceiptToBankLine?.(statementId, line?.id, entry.id);
        attached.push(entry);
      } catch (error) {
        attachFailed += 1;
        console.warn('Kunne ikke koble bilaget til banklinjen.', error);
      }
    }
    return { ...result, analyzed: analyze, attached, attachFailed };
  };

  // Én setning om hva som skjedde, med samme ordlyd uansett hvor filen ble
  // sluppet. `toast` eies av kallstedet — denne fila kjenner ikke UI-et.
  const uploadOutcomeMessage = ({ uploaded = [], failed = 0, skipped = 0 } = {}) => {
    if (!uploaded.length && !failed && skipped) {
      return { text: skipped === 1 ? 'Filen er verken bilde eller PDF, og kan ikke brukes som bilag.' : 'Filene er verken bilde eller PDF, og kan ikke brukes som bilag.', tone: 'error' };
    }
    if (!uploaded.length) return { text: failed === 1 ? 'Kunne ikke laste opp bilaget.' : 'Kunne ikke laste opp bilagene.', tone: 'error' };
    const rest = failed + skipped;
    if (rest) return { text: `${uploaded.length} av ${uploaded.length + rest} bilag ble lastet opp.`, tone: 'warning' };
    return { text: uploaded.length === 1 ? 'Bilag lastet opp.' : `${uploaded.length} bilag lastet opp.`, tone: 'success' };
  };

  const attachOutcomeMessage = ({ attached = [], attachFailed = 0, analyzed = true, failed = 0, skipped = 0 } = {}) => {
    if (!attached.length) return uploadOutcomeMessage({ uploaded: [], failed: failed + attachFailed, skipped });
    const suffix = analyzed ? ' Analyseres …' : '';
    const rest = attachFailed + failed + skipped;
    if (rest) return { text: `${attached.length} bilag koblet til banklinjen. ${rest} kom ikke med.`, tone: 'warning' };
    return {
      text: (attached.length === 1 ? 'Bilag koblet til banklinjen.' : `${attached.length} bilag koblet til banklinjen.`) + suffix,
      tone: 'success',
    };
  };

  globalThis.PlummeBankReceiptDrop = Object.freeze({
    hasFilePayload,
    isReceiptFile,
    receiptFilesFrom,
    bankLineIsHandled,
    uploadReceiptFiles,
    dropFilesOnBankLine,
    uploadOutcomeMessage,
    attachOutcomeMessage,
  });
})();
